import React, { useEffect, useRef } from 'react';

import Input from 'components/shared/Input/Input';
import Checkbox from 'components/shared/Checkbox/Checkbox';
import Typography from 'components/shared/Typography/Typography';

import usePracticeNameCheck from './lib/usePracticeNameCheck';

const isButton = (element) => !!element?.closest?.('button, [role="button"], [type="submit"]');

const PracticeNameInput = ({
  field,
  value,
  savedValue,
  onChange,
  keepAsTyped = false,
  onKeepAsTypedChange,
  registerNameCheck,
  disabled,
  ...inputProps
}) => {
  const { suggestion, check, gate, reset } = usePracticeNameCheck(field, savedValue);
  const checkboxId = `${inputProps.name || field}-keep-as-typed`;
  const latest = useRef({ value, disabled });
  latest.current = { value, disabled };

  useEffect(() => {
    if (savedValue !== undefined && value === savedValue) reset();
  }, [value, savedValue, reset]);

  useEffect(
    () =>
      registerNameCheck?.(field, () =>
        latest.current.disabled ? false : gate(latest.current.value)
      ),
    [field, registerNameCheck, gate]
  );

  const handleChange = (event) => {
    onChange?.(event);
    reset();
    if (keepAsTyped) onKeepAsTypedChange?.(false);
  };

  const handleBlur = (event) => {
    inputProps.onBlur?.(event);
    if (disabled) return;
    check(event?.target?.value, { bySaveGesture: isButton(event?.relatedTarget) });
  };

  return (
    <div className="flex flex-col gap-2">
      <Input
        {...inputProps}
        value={value}
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      {suggestion && !disabled && (
        <div className="flex flex-col gap-1.5" data-qa={`${checkboxId}-preview`}>
          <Typography variant="p" size="small" color="neutral-700">
            Will be saved as:{' '}
            <span className="font-500 text-neutral-900" data-dd-privacy="mask">
              {suggestion}
            </span>
          </Typography>
          <Checkbox
            id={checkboxId}
            label="Keep the name exactly as typed"
            isChecked={keepAsTyped}
            onChange={(event) => onKeepAsTypedChange?.(!!event?.target?.checked)}
          />
        </div>
      )}
    </div>
  );
};

export default PracticeNameInput;
